import { useAuth } from '../contexts/AuthContext';
import { Bell } from 'lucide-react';
import botHead from '../assets/bot_head.png';
import BrandLogo from './BrandLogo';

const PLAN_CHIP = {
  premium: 'bg-purple-50 text-brand-purple border-purple-200',
  trial:   'bg-teal-50 text-teal-700 border-teal-200',
  free:    'bg-gray-50 text-gray-500 border-gray-200',
};

function planLabel(planState) {
  if (!planState) return null;
  const ep = planState.effective_plan;
  if (ep === 'premium' && !planState.trial_active) return { label: 'PREMIUM', style: PLAN_CHIP.premium };
  if (planState.trial_active) return { label: `TRIAL · ${planState.trial_days_left ?? '?'}d`, style: PLAN_CHIP.trial };
  if (ep === 'free' || ep === 'none') return { label: 'FREE', style: PLAN_CHIP.free };
  return { label: (ep || 'FREE').toUpperCase(), style: PLAN_CHIP.free };
}

export default function TopBar({ title, onRobotClick }) {
  const { user, tenant, planState } = useAuth();
  const initials = user?.name?.split(' ').slice(0, 2).map(s => s[0]).join('').toUpperCase() || '?';
  const chip = planLabel(planState);

  return (
    <header
      data-testid="app-topbar"
      className="h-16 border-b border-gray-200 bg-white/80 backdrop-blur-xl flex items-center justify-between px-8"
    >
      <div className="flex items-center gap-3">
        <BrandLogo size={28} rounded className="flex-shrink-0" theme="light" />
        <h1 className="text-gray-900 font-extrabold text-xl tracking-tight" data-testid="topbar-title">
          {title}
        </h1>
      </div>
      <div className="flex items-center gap-3">
        {/* Tenant name + plan chip */}
        {tenant?.name && (
          <span data-testid="tenant-name" className="text-gray-500 text-sm font-semibold hidden sm:inline truncate max-w-[200px]">
            {tenant.name}
          </span>
        )}
        {chip && (
          <span
            data-testid="tenant-plan-chip"
            className={`px-3 py-1 rounded-full text-[10px] font-extrabold tracking-wider border ${chip.style}`}
          >
            {chip.label}
          </span>
        )}
        <button
          data-testid="notifications-btn"
          className="w-9 h-9 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          aria-label="Notifications"
        >
          <Bell size={16} strokeWidth={1.8} />
        </button>
        {/* Humanoid Robot — opens floating AI Assistant */}
        <button
          onClick={onRobotClick}
          data-testid="topbar-robot-btn"
          className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-50 to-teal-50 border border-gray-200 flex items-center justify-center cursor-pointer hover:scale-110 active:scale-95 transition-transform shadow-sm"
          aria-label="Open AI Assistant"
        >
          <img
            src={botHead}
            alt="AI Assistant"
            className="w-8 h-8 rounded-full object-cover"
          />
        </button>
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-purple to-brand-teal flex items-center justify-center">
          <span className="text-white text-xs font-bold">{initials}</span>
        </div>
      </div>
    </header>
  );
}
